import { produce } from "immer";
import { createContext, useContext, useMemo } from "react";

import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import useWorkspaceAccounts from "common/datahooks/useWorkspaceAccounts";
import useWorkspaceDetails from "common/datahooks/useWorkspaceDetails";
import useAppStore from "common/hooks/useAppStore";
import type { DetailedWorkspace, WorkspaceAccount } from "common/types";

interface WorkspaceDataProviderProps {
  workspaceId: string;
  children: ({
    isLoading,
    errorComponent,
  }: {
    isLoading: boolean;
    errorComponent: JSX.Element;
  }) => JSX.Element;
}

interface WorkspaceData extends DetailedWorkspace {
  accounts: WorkspaceAccount[];
}

const SelectedWorkspaceContext = createContext<WorkspaceData>(null);

export function SelectedWorkspaceProvider({
  workspaceId,
  children,
}: WorkspaceDataProviderProps) {
  const { workspace, isLoadingWorkspace, workspaceError, refetchWorkspace } =
    useWorkspaceDetails(workspaceId);
  const {
    workspaceAccounts,
    isLoadingWorkspaceAccounts,
    workspaceAccountsError,
    refetchWorkspaceAccounts,
  } = useWorkspaceAccounts(workspaceId);

  const isLoading = isLoadingWorkspace || isLoadingWorkspaceAccounts;

  let errorComponent: JSX.Element;
  if (workspaceError) {
    errorComponent = (
      <ErrorPlaceholder
        errorMessage="Error loading workspace details"
        onRetry={refetchWorkspace}
      />
    );
  } else if (workspaceAccountsError) {
    errorComponent = (
      <ErrorPlaceholder
        errorMessage="Error loading workspace accounts"
        onRetry={refetchWorkspaceAccounts}
      />
    );
  }

  const value = useMemo(() => {
    if (!workspace || !workspaceAccounts) return null;

    return { ...workspace, accounts: workspaceAccounts };
  }, [workspace, workspaceAccounts]);

  return (
    <SelectedWorkspaceContext.Provider value={value}>
      {children({ isLoading, errorComponent })}
    </SelectedWorkspaceContext.Provider>
  );
}

export function useSelectedWorkspaceContext() {
  const selectedWorkspace = useContext(SelectedWorkspaceContext);

  const {
    workspacePermissions,
    subscriptionStatus,
    isWithoutSubscription,
    isWithoutAccounts,
    isWithoutRentedSubscription,
    rentedSubscriptionStatus,
  } = useAppStore((state) => state.debugOptions);

  if (selectedWorkspace === undefined) {
    throw new Error(
      "useSelectedWorkspaceContext must be used inside SelectedWorkspaceProvider",
    );
  }

  if (selectedWorkspace) {
    return produce(selectedWorkspace, (draftWorkspace) => {
      if (workspacePermissions) {
        draftWorkspace.permissions = workspacePermissions;
      }

      if (isWithoutSubscription) {
        draftWorkspace.subscription = null;
      } else if (selectedWorkspace.subscription && subscriptionStatus) {
        draftWorkspace.subscription.status = subscriptionStatus;
      }

      if (isWithoutRentedSubscription) {
        draftWorkspace.rental_subscription = null;
      } else if (
        selectedWorkspace.rental_subscription &&
        rentedSubscriptionStatus
      ) {
        draftWorkspace.rental_subscription.status = rentedSubscriptionStatus;
      }

      if (isWithoutAccounts) {
        draftWorkspace.accounts_count = 0;
        draftWorkspace.accounts = [];
      }
    });
  }
}
